import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import type { InteractiveAnswer, QuestionBlockPayload } from '@/_shared/types';

import { questionAnswered, questionDismissed, questionSkipped } from './answer-helpers';
import { InteractiveCard } from './InteractiveCard';
import { MultiSelectList } from './MultiSelectList';
import { QuestionSubmitButton } from './QuestionSubmitButton';
import { buildMultiEntry, multiSubmitDisabledReason, multiTotal, validDefaults } from './selection';

interface Props {
  payload: QuestionBlockPayload;
  onResolve: (answer: InteractiveAnswer) => void;
  submitting?: boolean;
}

/**
 * Multi-select question block: prompt as title, checkbox list as body (via MultiSelectList, shared
 * with the wizard), a live "N selected" count on the footer left, Skip + Submit on the right.
 */
export function MultiSelectQuestion({ payload, onResolve, submitting }: Props): React.ReactElement {
  const question = payload.questions[0]!;
  const [checked, setChecked] = useState<Set<string>>(new Set(validDefaults(question)));
  const [otherChecked, setOtherChecked] = useState(false);
  const [otherText, setOtherText] = useState('');

  const disabledReason = multiSubmitDisabledReason(question, checked, otherChecked, otherText);
  const entry = buildMultiEntry(question, checked, otherChecked, otherText);
  // Same tally that drives `max`/`atMax` in the list, so a checked-but-empty Other reads as 1 in both
  // places. Submit stays disabled with a tooltip reason, which is where the empty row is explained.
  const selectedCount = multiTotal(checked, otherChecked);

  return (
    <InteractiveCard
      title={question.prompt}
      stats={payload.stats}
      onDismiss={() => onResolve(questionDismissed(payload.blockId))}
      dismissDisabled={submitting}
      busy={submitting}
      ariaLabel={question.prompt}
      footerLeft={<span className="text-xs text-muted-foreground">{selectedCount} selected</span>}
      footerRight={
        <>
          <Button variant="secondary" disabled={submitting} onClick={() => onResolve(questionSkipped(payload.blockId))}>
            Skip
          </Button>
          <QuestionSubmitButton
            disabledReason={disabledReason}
            loading={submitting}
            onClick={() => onResolve(questionAnswered(payload.blockId, [entry]))}
          >
            Submit
          </QuestionSubmitButton>
        </>
      }
    >
      <MultiSelectList
        question={question}
        checked={checked}
        onCheckedChange={setChecked}
        otherChecked={otherChecked}
        onOtherCheckedChange={setOtherChecked}
        otherText={otherText}
        onOtherTextChange={setOtherText}
        disabled={submitting}
      />
    </InteractiveCard>
  );
}
